import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
	FileText,
	Search,
	Calendar,
	Building2,
	ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@hiring-intelligence/ui';
import { Button } from '@hiring-intelligence/ui';
import { Input } from '@hiring-intelligence/ui';
import { jdApi } from '@/services/api';
import { useState } from 'react';

export default function JobDescriptionsPage() {
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(1);

	const { data: jdData, isLoading } = useQuery({
		queryKey: ['job-descriptions', page],
		queryFn: () => jdApi.list(page, 20)
	});

	const filteredJDs = jdData?.data?.filter((jd: any) =>
		jd.hiringRequest?.roleTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
		jd.hiringRequest?.company?.name?.toLowerCase().includes(searchQuery.toLowerCase())
	) || [];

	const getVersionBadge = (version: number) => (
		<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
			v{version}
		</span>
	);

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	};

	return (
		<div className="p-4 md:p-8">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
				<div>
					<h1 className="text-2xl font-bold">Job Descriptions</h1>
					<p className="text-muted-foreground">View and manage generated job descriptions</p>
				</div>
			</div>

			{/* Search */}
			<div className="mb-6">
				<div className="relative max-w-md">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search job descriptions..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-9"
					/>
				</div>
			</div>

			{/* JD List */}
			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{[1, 2, 3].map((i) => (
						<Card key={i} className="animate-pulse">
							<CardHeader className="space-y-2">
								<div className="h-4 bg-muted rounded w-3/4" />
								<div className="h-3 bg-muted rounded w-1/2" />
							</CardHeader>
							<CardContent>
								<div className="h-20 bg-muted rounded" />
							</CardContent>
						</Card>
					))}
				</div>
			) : filteredJDs.length > 0 ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{filteredJDs.map((jd: any) => (
						<Card key={jd.id} className="hover:shadow-md transition-shadow">
							<CardHeader>
								<div className="flex items-start justify-between">
									<div className="flex-1 min-w-0">
										<CardTitle className="text-lg truncate">
											{jd.hiringRequest?.roleTitle || 'Untitled Role'}
										</CardTitle>
										<div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
											<Building2 className="h-3 w-3" />
											<span className="truncate">
												{jd.hiringRequest?.company?.name || 'No company'}
											</span>
										</div>
									</div>
									{getVersionBadge(jd.version || 1)}
								</div>
							</CardHeader>
							<CardContent>
								<p className="text-sm text-muted-foreground line-clamp-3 mb-4">
									{jd.structuredContent?.summary ||
										jd.content?.substring(0, 150) ||
										'No description available...'}
								</p>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										<Calendar className="h-3 w-3" />
										{formatDate(jd.createdAt)}
									</div>
									<Button asChild size="sm" variant="outline">
										<Link to={`/job-descriptions/${jd.id}`}>
											<ExternalLink className="h-3 w-3 mr-1" />
											View
										</Link>
									</Button>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			) : (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-16">
						<FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
						<h3 className="text-lg font-medium mb-2">No job descriptions yet</h3>
						<p className="text-muted-foreground text-center max-w-md mb-4">
							Job descriptions will appear here once you generate them from hiring requests.
						</p>
						<Button asChild>
							<Link to="/hiring-requests">Go to Hiring Requests</Link>
						</Button>
					</CardContent>
				</Card>
			)}

			{/* Pagination */}
			{jdData?.meta && jdData.meta.totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-8">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setPage(p => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						Previous
					</Button>
					<span className="text-sm text-muted-foreground">
						Page {page} of {jdData.meta.totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setPage(p => p + 1)}
						disabled={page >= jdData.meta.totalPages}
					>
						Next
					</Button>
				</div>
			)}
		</div>
	);
}
